import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ExternalLink } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { CollectionDetailResponse } from '@aeris/contracts'
import { parseAmount } from '@aeris/finance'
import { formatMoney } from '@aeris/money'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  InlineAlert,
  Select,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@aeris/ui'

import {
  recordCollectionContact,
  sendCollectionTemplate,
} from '../lib/api/collections.ts'
import { ApiError, fetchTemplates } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { collectionMessage } from '../lib/collection-message.ts'
import { formatDate } from '../lib/date-format.ts'
import { formatPhone } from '../lib/phone-format.ts'
import { whatsappLink } from '../lib/whatsapp-link.ts'

import { TemplateComposer } from './inbox-template-composer.tsx'

/**
 * Cobrar pelo WhatsApp (F135), dos dois jeitos que o dono pediu.
 *
 * - **Do celular:** abre o WhatsApp de quem está cobrando com a mensagem já
 *   escrita (`wa.me`). Funciona para qualquer cliente com celular, sem modelo
 *   aprovado pela Meta — e a resposta chega naquele celular.
 * - **WhatsApp da loja:** pelo número oficial, num modelo de UTILIDADE
 *   aprovado; a resposta cai no Atendimento. Só aparece quando o cliente já tem
 *   conversa ligada ao cadastro, porque a Meta não deixa começar de outro jeito.
 *
 * Os dois ficam na linha do tempo do cliente.
 */

export const CollectionMessageDialog = ({
  detail,
  companyName,
  onClose,
}: {
  detail: CollectionDetailResponse
  companyName: string
  onClose: () => void
}): ReactNode => {
  const { allows } = useAuth()
  const queryClient = useQueryClient()
  const official =
    detail.whatsapp.length > 0 && allows('communication.message.send')

  const phones = detail.phones
  const [phone, setPhone] = useState(phones[0]?.value ?? '')
  const total =
    detail.standing.overdueAmount !== '0.00'
      ? detail.standing.overdueAmount
      : detail.standing.outstanding
  const [text, setText] = useState(() =>
    collectionMessage({
      customerName: detail.party.name,
      companyName,
      entries: detail.entries,
      total,
    })
  )
  const [accountId, setAccountId] = useState(
    detail.whatsapp[0]?.accountId ?? ''
  )
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)

  const link = whatsappLink(phone, text)

  const recordOpened = useMutation({
    mutationFn: async () =>
      await recordCollectionContact(detail.party.id, {
        channel: 'whatsapp',
        outcome: 'message_sent',
        note: text.slice(0, 2000),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['collections'] })
      onClose()
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? `O WhatsApp abriu, mas o contato não ficou registrado: ${cause.message}`
          : 'O WhatsApp abriu, mas o contato não ficou registrado.'
      )
    },
  })

  const templates = useQuery({
    queryKey: ['communication', 'templates', accountId],
    queryFn: ({ signal }) => fetchTemplates(accountId, signal),
    enabled: official && accountId !== '',
  })
  const usable = (templates.data ?? []).filter(
    (template) =>
      template.accountId === accountId &&
      template.status === 'approved' &&
      template.category === 'UTILITY'
  )

  const sendOfficial = useMutation({
    mutationFn: async (input: { templateId: string; values: string[] }) =>
      await sendCollectionTemplate(detail.party.id, {
        accountId,
        templateId: input.templateId,
        values: input.values,
      }),
    onSuccess: () => {
      setSent(true)
      void queryClient.invalidateQueries({ queryKey: ['collections'] })
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível enviar a mensagem.'
      )
    },
  })

  const firstOverdue = detail.entries.find((entry) => entry.daysOverdue > 0)
  const firstName =
    detail.party.name.trim().split(/\s+/)[0] ?? detail.party.name

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        title="Cobrar pelo WhatsApp"
        description={`${detail.party.name} · ${formatMoney(parseAmount(total))} em aberto`}
      >
        <Tabs defaultValue="phone">
          <TabsList>
            <TabsTrigger value="phone">Do celular</TabsTrigger>
            {official ? (
              <TabsTrigger value="official">WhatsApp da loja</TabsTrigger>
            ) : null}
          </TabsList>

          <TabsContent value="phone" className="flex flex-col gap-3 pt-3">
            {phones.length === 0 ? (
              <InlineAlert tone="warning">
                Este cliente não tem telefone no cadastro. Inclua um celular na
                ficha dele para cobrar pelo WhatsApp.
              </InlineAlert>
            ) : (
              <>
                {phones.length > 1 ? (
                  <Field label="Número">
                    {(field) => (
                      <Select
                        {...field}
                        value={phone}
                        onChange={(event) => {
                          setPhone(event.target.value)
                        }}
                      >
                        {phones.map((entry) => (
                          <option
                            key={`${entry.kind}-${entry.value}`}
                            value={entry.value}
                          >
                            {formatPhone(entry.value)}
                            {entry.label === null ? '' : ` · ${entry.label}`}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>
                ) : null}
                <Field
                  label="Mensagem"
                  hint="Dá para editar antes de abrir. O WhatsApp abre no aparelho de quem está cobrando."
                >
                  {(field) => (
                    <Textarea
                      {...field}
                      rows={9}
                      value={text}
                      onChange={(event) => {
                        setText(event.target.value)
                      }}
                    />
                  )}
                </Field>
                {link === null ? (
                  <InlineAlert tone="warning">
                    {`O número ${formatPhone(phone)} não parece um celular brasileiro válido.`}
                  </InlineAlert>
                ) : null}
              </>
            )}
            {error === null ? null : (
              <InlineAlert tone="danger">{error}</InlineAlert>
            )}
            <DialogFooter>
              <Button variant="secondary" onClick={onClose}>
                Fechar
              </Button>
              <Button
                variant="primary"
                disabled={link === null}
                loading={recordOpened.isPending}
                leadingIcon={
                  <ExternalLink aria-hidden="true" className="h-4 w-4" />
                }
                onClick={() => {
                  if (link === null) return
                  setError(null)
                  window.open(link, '_blank', 'noopener,noreferrer')
                  recordOpened.mutate()
                }}
              >
                Abrir WhatsApp
              </Button>
            </DialogFooter>
          </TabsContent>

          {official ? (
            <TabsContent value="official" className="flex flex-col gap-3 pt-3">
              {detail.whatsapp.length > 1 ? (
                <Field label="Número da loja">
                  {(field) => (
                    <Select
                      {...field}
                      value={accountId}
                      onChange={(event) => {
                        setAccountId(event.target.value)
                      }}
                    >
                      {detail.whatsapp.map((entry, index) => (
                        <option key={entry.accountId} value={entry.accountId}>
                          {`Conta ${String(index + 1)}`}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              ) : null}
              {sent ? (
                <InlineAlert tone="success" title="Mensagem na fila de envio">
                  A resposta do cliente chega no Atendimento.
                </InlineAlert>
              ) : (
                <TemplateComposer
                  templates={usable}
                  now={new Date()}
                  sending={sendOfficial.isPending}
                  error={error}
                  onSend={(input) => {
                    setError(null)
                    sendOfficial.mutate(input)
                  }}
                  prefill={(template) =>
                    template.variables.map((variable) => {
                      const name = variable.name.toLowerCase()
                      if (name.includes('nome')) return firstName
                      if (/valor|total|quantia|d[ée]bito/.test(name)) {
                        return formatMoney(parseAmount(total))
                      }
                      if (
                        /venc|data/.test(name) &&
                        firstOverdue !== undefined
                      ) {
                        return formatDate(firstOverdue.dueAt)
                      }
                      if (name.includes('loja') || name.includes('empresa')) {
                        return companyName
                      }
                      return ''
                    })
                  }
                />
              )}
              {usable.length === 0 && !templates.isPending ? (
                <p className="text-(length:--text-xs) text-(--text-muted)">
                  Cobrança só vai em modelo de utilidade aprovado. Cadastre um
                  em Atendimento → Modelos.
                </p>
              ) : null}
            </TabsContent>
          ) : null}
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
